import { useEffect, useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import api from "@/lib/api";
import { UsersThree, Plus, CheckCircle, Warning, X, Info } from "@phosphor-icons/react";
import SeatPicker from "@/components/billing/SeatPicker";

// Cache the Stripe.js instance across renders — loadStripe is expensive
// and Stripe recommends calling it once per pub key. We lazy-load using
// the key returned by the seat-purchase response so we don't ship the
// pub key in the bundle.
let _stripePromise = null;
const getStripe = (publishableKey) => {
  if (!publishableKey) return null;
  if (!_stripePromise) _stripePromise = loadStripe(publishableKey);
  return _stripePromise;
};

/**
 * ManageSeatsCard — shows current seat usage, lets admins purchase more or downgrade.
 *
 * Upgrade (target > current): preview → confirm → charge via /billing/seats/purchase
 *   - Returns 402 if no default payment method or card declined.
 *   - Surfaces the prorated cost + tax + mode (immediate vs proration) before charge.
 * Downgrade (target < current): direct /billing/seats POST (no charge).
 */
export default function ManageSeatsCard() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/billing/seats");
      setData(r.data);
      setTarget(r.data.seats_total);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downgrade = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.post("/billing/seats", { seats_total: target });
      setOk(`Seat count reduced to ${target}.`);
      setEditing(false);
      await load();
      setTimeout(() => setOk(""), 4000);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/billing/seats/preview", { add_seats: target - data.seats_total });
      setPreview(r.data.preview);
      setConfirmOpen(true);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmPurchase = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const r = await api.post("/billing/seats/purchase", { add_seats: preview.add_seats });
      const status = r.data.payment_status;
      const seatTxId = r.data.seat_transaction_id;

      // ─── On-session CIT flow (iter152): client_secret confirmation ─
      // Backend returns `requires_client_action=true` for a live PI that
      // needs the browser to confirm via Stripe.js. This is how the
      // admin's active "Pay $38.00" click becomes a customer-initiated
      // on-session charge (compliant with Stripe's India card rules —
      // no mandate required for one-off CITs). 3DS challenges surface
      // inline; we never touch the subscription mandate.
      if (r.data.requires_client_action && r.data.client_secret) {
        const stripe = await getStripe(r.data.publishable_key);
        if (!stripe) {
          throw new Error("Stripe.js could not initialise — publishable key missing");
        }
        const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(
          r.data.client_secret,
        );
        if (stripeErr) {
          setErr(stripeErr.message || "Card confirmation failed");
          return;
        }
        if (!paymentIntent || paymentIntent.status !== "succeeded") {
          setErr(`Payment status: ${paymentIntent?.status || "unknown"} — please retry.`);
          return;
        }
        // Server-side verification + seat apply. Server re-fetches the
        // PI from Stripe and won't apply the seat unless Stripe itself
        // reports `succeeded` — the client's claim is never trusted.
        const c = await api.post("/billing/seats/purchase/confirm", {
          seat_transaction_id: seatTxId,
        });
        if (c.data?.ok) {
          setOk(`Charged $${(preview.total_charge_now).toFixed(2)} — ${preview.add_seats} seat(s) activated.`);
        } else {
          setErr(c.data?.message || "Server could not confirm payment — please retry.");
          return;
        }
      } else if (status === "succeeded") {
        setOk(`Charged $${(preview.total_charge_now).toFixed(2)} — ${preview.add_seats} seat(s) activated.`);
      } else if (status === "deferred") {
        setOk(`${preview.add_seats} seat(s) activated. Prorated $${preview.total_on_next_invoice.toFixed(2)} will appear on your next invoice.`);
      } else if (status === "skipped_zero") {
        setOk(`${preview.add_seats} seat(s) added (no charge).`);
      } else {
        setOk(`Seats updated. Payment status: ${status}.`);
      }
      setConfirmOpen(false);
      setEditing(false);
      setPreview(null);
      await load();
      setTimeout(() => setOk(""), 6000);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      // Keep the modal open so user can retry / fix payment method
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    if (target > data.seats_total) return openPreview();
    if (target < data.seats_total) return downgrade();
  };

  if (!data) {
    return <div className="card p-6 animate-pulse" data-testid="manage-seats-loading"><div className="h-4 bg-[var(--bg-sidebar)] w-1/3 mb-3" /><div className="h-10 bg-[var(--bg-sidebar)]" /></div>;
  }

  const pct = data.seats_total > 0 ? Math.min(100, (data.seats_used / data.seats_total) * 100) : 0;
  const atLimit = data.seats_available <= 0;

  return (
    <div className="card p-6" data-testid="manage-seats-card">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <UsersThree size={18} weight="fill" className="text-[var(--accent)]" />
            <div className="font-display font-bold text-[18px]">Manage Seats</div>
          </div>
          <div className="text-[12px] text-[var(--text-secondary)] mt-1">
            Each seat = 1 team member. Add or remove seats anytime; price adjusts immediately.
          </div>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} className="btn-ghost text-[12px]" data-testid="manage-seats-edit-btn">
            Adjust
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 mb-5">
        <Stat label="USED" value={data.seats_used} testid="seats-used" />
        <Stat label="TOTAL" value={data.seats_total} testid="seats-total-stat" />
        <Stat label="AVAILABLE" value={data.seats_available} accent={atLimit ? "danger" : "success"} testid="seats-available" />
      </div>

      {/* Usage bar */}
      <div className="mb-5">
        <div className="h-2 bg-[var(--bg-sidebar)] border border-[var(--border)] overflow-hidden">
          <div className={`h-full transition-all ${atLimit ? "bg-[var(--danger)]" : pct > 80 ? "bg-amber-500" : "bg-[var(--accent)]"}`}
               style={{ width: `${pct}%` }} data-testid="seats-usage-bar" />
        </div>
        <div className="flex items-center justify-between mt-1.5 font-mono text-[10px] text-[var(--text-muted)]">
          <span>{data.seats_used} of {data.seats_total} seats in use</span>
          <span>${data.monthly_total.toLocaleString()}/mo · {data.plan} plan</span>
        </div>
      </div>

      {editing && (
        <div className="border-t border-[var(--border)] pt-5 space-y-4" data-testid="manage-seats-editor">
          <SeatPicker
            seats={target}
            onChange={setTarget}
            pricePerSeat={data.price_per_seat}
            minSeats={data.seats_used}
            label="New seat count"
          />
          {err && (
            <div className="border border-[var(--danger)] bg-red-50 text-[var(--danger)] text-[12px] p-3 flex items-start gap-2" data-testid="manage-seats-error">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
              <div>{err}</div>
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={() => { setEditing(false); setTarget(data.seats_total); setErr(""); }}
                    disabled={busy} className="btn-ghost flex-1" data-testid="manage-seats-cancel">
              Cancel
            </button>
            <button onClick={save}
                    disabled={busy || target === data.seats_total}
                    className="btn-primary flex-1 disabled:opacity-40"
                    data-testid="manage-seats-save">
              {busy ? "Updating…" : target > data.seats_total ? `Add ${target - data.seats_total} seat${target - data.seats_total === 1 ? "" : "s"}` : target < data.seats_total ? `Reduce to ${target}` : "Update"}
            </button>
          </div>
          <div className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-widest">
            Upgrading? You'll see a preview before any charge.
          </div>
        </div>
      )}

      {ok && !editing && (
        <div className="border border-[var(--success)] bg-emerald-50 text-[var(--success)] text-[12px] p-3 flex items-start gap-2" data-testid="manage-seats-success">
          <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
          <div>{ok}</div>
        </div>
      )}

      {!editing && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              // One-click add: bump target by 1 and open the preview modal
              // directly — admins asking for a single seat should not have
              // to click through the picker.
              setTarget(data.seats_total + 1);
              setBusy(true); setErr("");
              (async () => {
                try {
                  const r = await api.post("/billing/seats/preview", { add_seats: 1 });
                  setPreview(r.data.preview);
                  setEditing(true);
                  setConfirmOpen(true);
                } catch (e) {
                  setErr(e.response?.data?.detail || e.message);
                  setEditing(true);
                } finally {
                  setBusy(false);
                }
              })();
            }}
            className="w-full flex items-center justify-center gap-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white py-2.5 font-semibold text-[13px] transition-colors"
            data-testid="manage-seats-add-one-btn"
          >
            <Plus size={14} weight="bold" /> Add 1 seat
          </button>
          <button onClick={() => setEditing(true)}
                  className="w-full flex items-center justify-center gap-2 bg-[var(--accent)] hover:opacity-90 text-white py-2.5 font-semibold text-[13px] transition-opacity"
                  data-testid="manage-seats-buy-btn">
            <Plus size={14} weight="bold" /> Buy more seats
          </button>
        </div>
      )}

      {confirmOpen && preview && (
        <SeatPurchasePreviewModal
          preview={preview}
          busy={busy}
          err={err}
          onClose={() => { setConfirmOpen(false); setPreview(null); setErr(""); }}
          onConfirm={confirmPurchase}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  const color = accent === "danger" ? "text-[var(--danger)]" : accent === "success" ? "text-[var(--success)]" : "text-[var(--text-primary)]";
  return (
    <div className="border border-[var(--border)] p-3 text-center" data-testid={testid}>
      <div className="font-mono text-[9px] tracking-widest text-[var(--text-muted)]">{label}</div>
      <div className={`font-display font-extrabold text-[28px] leading-none mt-1 ${color}`}>{value}</div>
    </div>
  );
}

/**
 * SeatPurchasePreviewModal — "are you sure?" gate before charging.
 *
 * Shows current → new seat count, per-seat price, proration math, tax, total,
 * billing mode (immediate vs proration), and the effective date. Requires
 * explicit Confirm before posting to /billing/seats/purchase.
 */
function SeatPurchasePreviewModal({ preview, busy, err, onClose, onConfirm }) {
  const isImmediate = preview.mode === "immediate";
  const chargeNow = preview.total_charge_now;
  const nextInvoice = preview.total_on_next_invoice;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="seat-preview-modal">
      <div className="bg-white max-w-md w-full border border-[var(--border)] shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">Confirm purchase</div>
            <div className="text-base font-medium mt-0.5">Add {preview.add_seats} seat{preview.add_seats === 1 ? "" : "s"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bg-elev)]" data-testid="seat-preview-close-btn">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm">
          <Row label="Current seats" value={preview.current_seats} />
          <Row label="New seats" value={preview.new_seats} accent />
          <Row label="Plan" value={(preview.plan || "").toUpperCase()} />
          <Row label="Per-seat (monthly)" value={`$${preview.per_seat_monthly}`} />
          <Row
            label="Proration"
            value={`${preview.days_remaining}/${preview.days_in_period} days · $${preview.prorated_per_seat.toFixed(2)}/seat`}
          />
          <div className="border-t border-[var(--border)] pt-3">
            <Row label="Subtotal" value={`$${preview.subtotal.toFixed(2)}`} />
            <Row label={`Tax (${preview.tax_rate_pct}%)`} value={`$${preview.tax.toFixed(2)}`} />
            <Row
              label={isImmediate ? "Total charged now" : "Added to next invoice"}
              value={`$${(isImmediate ? chargeNow : nextInvoice).toFixed(2)}`}
              accent
              big
            />
          </div>

          <div className="flex items-start gap-2 text-xs text-[var(--text-muted)] mt-2 p-3 bg-[var(--bg-elev)] border border-[var(--border)]" data-testid="seat-preview-mode-note">
            <Info size={14} className="mt-0.5 shrink-0" />
            <div>
              <span className="font-mono uppercase tracking-widest">{preview.mode}</span> billing mode —{" "}
              {isImmediate
                ? "your default card will be charged immediately. Seats activate on successful payment."
                : "seats activate now; the prorated charge will appear on your next monthly invoice."}
            </div>
          </div>

          {err && (
            <div className="border border-[var(--danger)] bg-red-50 text-[var(--danger)] text-xs p-3 flex items-start gap-2" data-testid="seat-preview-error">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
              <div>{err}</div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[var(--border)] flex gap-2">
          <button onClick={onClose} disabled={busy} className="flex-1 px-3 py-2 border border-[var(--border)] text-sm" data-testid="seat-preview-cancel-btn">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy} className="flex-1 px-3 py-2 bg-[var(--accent)] text-white text-sm font-medium disabled:opacity-50" data-testid="seat-preview-confirm-btn">
            {busy ? "Processing…" : isImmediate ? `Pay $${chargeNow.toFixed(2)}` : `Confirm`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent, big }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[var(--text-muted)]">{label}</span>
      <span className={`${accent ? "font-medium text-[var(--text-primary)]" : ""} ${big ? "text-base" : ""} font-mono`}>{value}</span>
    </div>
  );
}
